import React, { createContext, useCallback, useContext } from "react";

const OwnershipJurisdictionContext = createContext(null);

export function OwnershipLabelsProvider({ jurisdiction, children }) {
  return <OwnershipJurisdictionContext.Provider value={jurisdiction}>{children}</OwnershipJurisdictionContext.Provider>;
}

// Presentation only. Never use these labels as stored entity types or assessment inputs.
export function ownershipDisplayText(text, jurisdiction) {
  if (typeof text !== "string") return text;
  const geo = String(jurisdiction ?? "").trim().toLowerCase();
  const uae = ["uae", "ae", "are", "united arab emirates"].includes(geo);
  const swiss = ["switzerland", "ch", "che"].includes(geo);
  if (!uae && !swiss) return text;
  return text.replace(/\b(?:domiciliary|personal investment) company\b/gi, match => {
    const label = uae ? "personal investment company" : "domiciliary company";
    return /^[A-Z]/.test(match) ? label[0].toUpperCase() + label.slice(1) : label;
  });
}

// Limit diagram replacement to the type caption inside HTML labels. Names and
// the canonical snapshot (including PIC and initialAHType) are left untouched.
export function ownershipDiagramText(source, jurisdiction) {
  return String(source || "").replace(
    /(<(?:span|div)\b[^>]*font-weight:\s*(?:600|700)[^>]*>(?:(?!<\/(?:span|div)>)[\s\S])*?)((?:Domiciliary|Personal Investment|Personal investment) [Cc]ompany \(PIC\))(?=<)/g,
    (_, prefix, label) => prefix + ownershipDisplayText(label, jurisdiction)
  );
}

export function useOwnershipLabels(explicitJurisdiction) {
  const inherited = useContext(OwnershipJurisdictionContext);
  const jurisdiction = explicitJurisdiction ?? inherited;
  const ownershipLabel = useCallback(text => ownershipDisplayText(text, jurisdiction), [jurisdiction]);
  const ownershipDiagram = useCallback(source => ownershipDiagramText(source, jurisdiction), [jurisdiction]);
  return { ownershipLabel, ownershipDiagram, jurisdiction };
}
